import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Typography,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Chip,
  Box,
} from '@mui/material';
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaGlobe } from 'react-icons/fa';
import { API_BASE_URL } from '../constants/APIConstants';
import { auth } from '../firebase';
import './adminDashboard.css';

const AccountDetails = ({ account, onStatusChange }) => {
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [accountStatus, setAccountStatus] = useState(account.status || 'pending');
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [isRejecting, setIsRejecting] = useState(false);
  const [otherReason, setOtherReason] = useState('');
  const [showOtherReasonField, setShowOtherReasonField] = useState(false);
  const [loadedSocialLinks, setLoadedSocialLinks] = useState(account.socialLinks || account.socials || account.socialMediaLinks || {});
  const [isLoadingSocialLinks, setIsLoadingSocialLinks] = useState(false);

  useEffect(() => {
    const existingSocialLinks = account.socialLinks || account.socials || account.socialMediaLinks || {};
    setLoadedSocialLinks(existingSocialLinks);

    const hasExistingLinks = Object.entries(existingSocialLinks).some(
      ([platform, url]) => platform !== 'updatedAt' && typeof url === 'string' && url.trim(),
    );

    if (hasExistingLinks || !account.accountId || account.accountId === 'N/A') {
      return;
    }

    const fetchSocialLinks = async () => {
      setIsLoadingSocialLinks(true);
      try {
        const token = await auth.currentUser.getIdToken();
        const response = await axios.get(
          `${API_BASE_URL}/api/data/get/social/links`,
          {
            params: { accountId: account.accountId },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setLoadedSocialLinks(response.data || {});
      } catch (error) {
        console.error('Error fetching social links:', error);
      } finally {
        setIsLoadingSocialLinks(false);
      }
    };

    fetchSocialLinks();
  }, [account]);

  const handleApprove = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      // accountVerified and waitlistStatus are two separate fields on the
      // account doc, and the app's access gate checks waitlistStatus - this
      // call used to only update accountVerified, which left approved
      // accounts stuck locked out since waitlistStatus never changed from
      // "pending". Both must be updated, same as the table row's Approve
      // button already does correctly.
      await axios.put(
        `${API_BASE_URL}/api/admin/updateWaitlistStatus`,
        {},
        {
          params: { accountId: account.accountId, status: 'approved' },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await axios.put(
        `${API_BASE_URL}/api/admin/updateVerificationStatus?accountId=${account.accountId}&status=approved`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setFeedbackMessage('Account has been approved!');
      setAccountStatus('approved');
      setShowFeedback(true);
      onStatusChange?.(account.accountId, 'approved');
    } catch (error) {
      setFeedbackMessage('Failed to approve account.');
      setShowFeedback(true);
    }
  };

  const handleRejectClick = () => {
    setIsRejectionDialogOpen(true);
  };

  const handleRejectConfirm = async (reasons) => {
    setIsRejecting(true);
    try {
      const finalReasons = showOtherReasonField && otherReason
        ? [...reasons.filter(r => r !== "Other"), otherReason]
        : reasons;

      const token = await auth.currentUser.getIdToken();
      const reasonsParam = encodeURIComponent(finalReasons.join(','));
      // Same fix as handleApprove: waitlistStatus must be updated alongside
      // accountVerified, not just accountVerified alone.
      await axios.put(
        `${API_BASE_URL}/api/admin/updateWaitlistStatus`,
        {},
        {
          params: { accountId: account.accountId, status: 'rejected' },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await axios.put(
        `${API_BASE_URL}/api/admin/updateVerificationStatus?accountId=${account.accountId}&status=rejected&reasons=${reasonsParam}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setFeedbackMessage('Account has been rejected!');
      setAccountStatus('rejected');
      setShowFeedback(true);
      onStatusChange?.(account.accountId, 'rejected');
    } catch (error) {
      setFeedbackMessage('Failed to reject account.');
      setShowFeedback(true);
    } finally {
      setIsRejecting(false);
      setIsRejectionDialogOpen(false);
      setSelectedReasons([]);
      setOtherReason('');
      setShowOtherReasonField(false);
    }
  };

  const handleRejectCancel = () => {
    setIsRejectionDialogOpen(false);
    setSelectedReasons([]);
    setOtherReason('');
    setShowOtherReasonField(false);
  };

  const handleCloseFeedback = () => {
    setShowFeedback(false);
  };

  const handleCheckboxChange = (reason, checked) => {
    if (reason === "Other") {
      setShowOtherReasonField(checked);
      if (!checked) {
        setOtherReason('');
      }
    }
    
    const updatedReasons = checked
      ? [...selectedReasons, reason]
      : selectedReasons.filter(r => r !== reason);
    setSelectedReasons(updatedReasons);
  };

  const getSocialMediaIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return <FaFacebook size={24} />;
      case 'twitter':
        return <FaTwitter size={24} />;
      case 'linkedin':
        return <FaLinkedin size={24} />;
      case 'instagram':
        return <FaInstagram size={24} />;
      case 'youtube':
        return <FaYoutube size={24} />;
      case 'website':
        return <FaGlobe size={24} />;
      default:
        return null;
    }
  };

  const portfolioImages = (account.portfolioCollections || []).flatMap(
    (collection) => collection.portfolioDocuments || [],
  );
  const normalizeUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return '';
    return /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
  };
  const socialLinksSource = loadedSocialLinks || {};
  const socialLinks = Object.entries(socialLinksSource)
    .filter(([platform, url]) => platform !== 'updatedAt' && typeof url === 'string' && url.trim())
    .map(([platform, url]) => [platform, normalizeUrl(url)]);
  const statusColor = accountStatus === 'approved' ? 'success' : accountStatus === 'rejected' ? 'error' : 'warning';
  const hasEmail = Boolean(account.email && account.email !== 'N/A');
  const hasPortfolio = portfolioImages.length > 0;
  const hasSocialLinks = socialLinks.length > 0;
  const reviewSignals = [
    ['Email', hasEmail ? 'Present' : 'Missing', hasEmail],
    ['Portfolio', hasPortfolio ? `${portfolioImages.length} images` : 'Missing', hasPortfolio],
    ['Social', hasSocialLinks ? `${socialLinks.length} linked` : 'Missing', hasSocialLinks],
  ];

  return (
    <div className="profile-review">
      <section className="profile-review__hero">
        <div className="profile-review__identity">
          <Avatar
            src={account.accountProfileURL}
            alt="Profile"
            className="profile-review__avatar"
          />
          <div>
            <div className="profile-review__eyebrow">Profile Review</div>
            <h2 className="profile-review__name">{account.name || 'Unnamed account'}</h2>
            <div className="profile-review__meta">{account.email || 'No email provided'}</div>
            <div className="profile-review__meta">{account.contact || 'No phone provided'}</div>
            <div className="profile-review__signal-row">
              {reviewSignals.map(([label, value, isReady]) => (
                <span
                  className={`profile-review__signal ${isReady ? 'profile-review__signal--ready' : 'profile-review__signal--missing'}`}
                  key={label}
                >
                  <span>{label}</span>
                  <strong>{value}</strong>
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="profile-review__hero-side">
          <div className="profile-review__review-label">Current Status</div>
          <Chip
            className="profile-review__status"
            label={accountStatus}
            color={statusColor}
            variant="filled"
          />
        </div>
      </section>

      <section className="profile-review__stats">
        <div className="profile-review__stat">
          <div className="profile-review__stat-index">01</div>
          <span>Category</span>
          <strong>{account.accountCategory || 'Not Provided'}</strong>
        </div>
        <div className="profile-review__stat">
          <div className="profile-review__stat-index">02</div>
          <span>Sub Category</span>
          <strong>{account.accountSubCategory || 'Not Provided'}</strong>
        </div>
        <div className="profile-review__stat">
          <div className="profile-review__stat-index">03</div>
          <span>Portfolio</span>
          <strong>{portfolioImages.length} images</strong>
        </div>
        <div className="profile-review__stat">
          <div className="profile-review__stat-index">04</div>
          <span>Social Links</span>
          <strong>{socialLinks.length} linked</strong>
        </div>
      </section>

      <section className="profile-review__grid">
        <div className="profile-review__panel profile-review__panel--wide">
          <div className="profile-review__panel-header">
            <div>
              <div className="profile-review__panel-kicker">Identity</div>
              <h3>Account Details</h3>
            </div>
          </div>
          <div className="profile-review__details-grid">
            {[
              ['Phone', account.contact || 'Not Provided'],
              ['City', account.accountCity || 'Not Provided'],
              ['Date of Birth', account.dob || 'Not Provided'],
              ['Email', account.email || 'Not Provided'],
            ].map(([label, value]) => (
              <div className="profile-review__detail" key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
          <div className="profile-review__description">
            <span>Description</span>
            <p>{account.accountDescription || 'No Description Available'}</p>
          </div>
        </div>

        <div className="profile-review__panel">
          <div className="profile-review__panel-header">
            <div>
              <div className="profile-review__panel-kicker">Network</div>
              <h3>Social Links</h3>
            </div>
          </div>
          {isLoadingSocialLinks ? (
            <div className="profile-review__empty">
              <CircularProgress size={20} />
              <span style={{ marginLeft: '10px' }}>Loading social links...</span>
            </div>
          ) : socialLinks.length > 0 ? (
            <div className="profile-review__social-list">
              {socialLinks.map(([platform, url]) => (
                <a href={url} target="_blank" rel="noopener noreferrer" className="profile-review__social-link" key={platform}>
                  <span className="profile-review__social-icon">{getSocialMediaIcon(platform) || <FaGlobe size={22} />}</span>
                  <span>{platform.charAt(0).toUpperCase() + platform.slice(1)}</span>
                  <small>Open</small>
                </a>
              ))}
            </div>
          ) : (
            <div className="profile-review__empty">No social media links available.</div>
          )}
        </div>
      </section>

      <section className="profile-review__panel">
        <div className="profile-review__panel-header">
          <div>
            <div className="profile-review__panel-kicker">Work Samples</div>
            <h3>Portfolio Images</h3>
          </div>
          <Chip label={`${portfolioImages.length} images`} size="small" variant="outlined" />
        </div>
        {portfolioImages.length > 0 ? (
          <div className="profile-review__portfolio-grid">
            {portfolioImages.map((document, index) => (
              <a
                href={document.imageURL}
                target="_blank"
                rel="noopener noreferrer"
                className="profile-review__portfolio-item"
                key={`${document.imageURL || 'portfolio'}-${index}`}
              >
                <img
                  src={document.imageURL}
                  alt="Portfolio"
                  className="profile-review__portfolio-image"
                />
                <span>View sample {index + 1}</span>
              </a>
            ))}
          </div>
        ) : (
          <div className="profile-review__empty">No portfolio images available.</div>
        )}
      </section>

      <section className="profile-review__actions">
        <Box>
          <div className="profile-review__panel-kicker">Review Decision</div>
          <Typography variant="body2" color="text.secondary">
            Approve or reject this account after reviewing the information above.
          </Typography>
        </Box>
        {accountStatus === 'approved' ? (
          <Chip color="success" label="Account approved" variant="filled" />
        ) : accountStatus === 'rejected' ? (
          <Chip color="error" label="Account rejected" variant="filled" />
        ) : (
          <Box className="profile-review__action-buttons">
            <Button variant="outlined" color="error" onClick={handleRejectClick}>
              Reject
            </Button>
            <Button variant="contained" color="success" onClick={handleApprove}>
              Approve
            </Button>
          </Box>
        )}
      </section>

      {/* Rejection Reasons Dialog */}
      <Dialog open={isRejectionDialogOpen} onClose={handleRejectCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Select Rejection Reasons</DialogTitle>
        <DialogContent>
          <FormGroup>
            {[
              "Your details provided are inaccurate (name, email, website)",
              "You have chosen wrong category for your account",
              "Invalid Profile Picture",
              "We found your account to be suspicious",
              "Other"
            ].map((reason) => (
              <FormControlLabel
                key={reason}
                control={
                  <Checkbox
                    checked={selectedReasons.includes(reason)}
                    onChange={(e) => handleCheckboxChange(reason, e.target.checked)}
                  />
                }
                label={reason}
              />
            ))}
            {showOtherReasonField && (
              <TextField
                fullWidth
                multiline
                rows={2}
                margin="normal"
                label="Please specify other reason"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                error={showOtherReasonField && !otherReason}
                helperText={showOtherReasonField && !otherReason ? "Please specify the reason" : ""}
              />
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel}>Cancel</Button>
          <Button 
            onClick={() => handleRejectConfirm(selectedReasons)} 
            color="error" 
            disabled={
              selectedReasons.length === 0 || 
              isRejecting || 
              (showOtherReasonField && !otherReason)
            }
          >
            {isRejecting ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={showFeedback} autoHideDuration={6000} onClose={handleCloseFeedback}>
        <Alert onClose={handleCloseFeedback} severity="info">
          {feedbackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AccountDetails;
